import type { ComponentProps } from 'react';
import { type IStyleParam, type StyleParamType } from '@wix/tpa-settings';
import type { Dock } from '@wix/wix-base-ui';

type GetStyleParamType = {
  isMobile: boolean;
  getStyleParamValue: (param: IStyleParam) => any;
  paramName: IStyleParam;
  mobileParamName: IStyleParam;
  dependencyParam: IStyleParam;
};
/**
 * Helps to move away from using separate style params for mobile and desktop.
 *
 * Resolves the default value based on legacy style params created with `createStylesParam()`.
 *
 * @example
 * export default createStylesParams({
 *   showNewestPostsFirstV2: {
 *     type: StyleParamType.Boolean,
 *     getDefaultValue: ({ isMobile, getStyleParamValue }) =>
 *       resolveDefaultStyleParamValue({
 *         isMobile,
 *         getStyleParamValue,
 *         paramName: LEGACY_STYLE_PARAMS.showNewestPostsFirst,
 *         mobileParamName: LEGACY_STYLE_PARAMS.showNewestPostsFirstMobile,
 *         dependencyParam: LEGACY_STYLE_PARAMS.isMobileDisplaySettingsEnabled,
 *       }),
 *   },
 * });
 */
export const resolveDefaultStyleParamValue = ({
  isMobile,
  getStyleParamValue,
  paramName,
  mobileParamName,
  dependencyParam,
}: GetStyleParamType) => {
  if (isMobile) {
    const isMobileSettingsEnabled = getStyleParamValue(dependencyParam);
    if (isMobileSettingsEnabled) {
      const mobileParamValue = getStyleParamValue(mobileParamName);
      if (typeof mobileParamValue !== 'undefined') {
        return mobileParamValue;
      }
    }
  }
  return getStyleParamValue(paramName);
};

export const enum AlignmentValue {
  Start = 0,
  Center = 1,
  End = 2,
}

export const getFlexAlignmentValue = (alignment: AlignmentValue, isRTL: boolean) => {
  const alignmentMap = {
    [AlignmentValue.Start]: isRTL ? 'flex-end' : 'flex-start',
    [AlignmentValue.Center]: 'center',
    [AlignmentValue.End]: isRTL ? 'flex-start' : 'flex-end',
  } as const satisfies Record<number, string>;

  return alignmentMap[alignment];
};

export const getTextAlignmentValue = (alignment: AlignmentValue, isRTL: boolean) => {
  const alignmentMap = {
    [AlignmentValue.Start]: isRTL ? 'end' : 'start',
    [AlignmentValue.Center]: 'center',
    [AlignmentValue.End]: isRTL ? 'start' : 'end',
  } as const satisfies Record<number, string>;

  return alignmentMap[alignment];
};

export function getFontCssValue(
  fontParamValue: FontValue,
  property: 'font',
): Extract<React.CSSProperties['font'], string>;
export function getFontCssValue(
  fontParamValue: FontValue,
  property: 'textDecoration',
): Extract<React.CSSProperties['textDecoration'], string>;
export function getFontCssValue(
  fontParamValue: FontValue,
  property: 'font' | 'textDecoration',
): Extract<React.CSSProperties['font'] | React.CSSProperties['textDecoration'], string> {
  if (property === 'textDecoration') {
    return fontParamValue.underline ? 'underline' : 'none';
  }

  /** @example "sans-serif;text-decoration:underline;" -> "sans-serif" */
  const stripCss = (font: string) => font.replace(/;.*$/g, '');

  const fontFamily = fontParamValue.family.map(stripCss).join(', ');
  const fontLineHeight = fontParamValue.lineHeight ?? 1.5;
  const fontSize = fontParamValue.size ?? 16;
  const fontStyle = fontParamValue.style ?? 'normal';
  const fontWeight = fontParamValue.weight ?? 'normal';
  const fontVariant = fontParamValue.variant ?? 'normal';

  return `${fontStyle} ${fontVariant} ${fontWeight} ${fontSize}/${fontLineHeight} ${fontFamily}`;
}

type FontValue = {
  lineHeight: string;
  stretch: string;
  style: string;
  variant: string;
  weight: string;
  size: string;
  family: string[];
  underline: boolean;
  supports: {
    uppercase: boolean;
  };
};

export const enum DockDirection {
  TOP_LEFT = 1,
  TOP = 2,
  TOP_RIGHT = 3,
  LEFT = 4,
  MIDDLE = 5,
  RIGHT = 6,
  BOTTOM_LEFT = 7,
  BOTTOM = 8,
  BOTTOM_RIGHT = 9,
}

type BaseUiDockDirection = NonNullable<ComponentProps<typeof Dock>['value']>;

export function toBaseUiDockDirection(direction: DockDirection): BaseUiDockDirection {
  const directionMap: Record<DockDirection, BaseUiDockDirection> = {
    [DockDirection.TOP_LEFT]: 'TOP_LEFT',
    [DockDirection.TOP]: 'TOP',
    [DockDirection.TOP_RIGHT]: 'TOP_RIGHT',
    [DockDirection.LEFT]: 'LEFT',
    [DockDirection.MIDDLE]: 'MIDDLE',
    [DockDirection.RIGHT]: 'RIGHT',
    [DockDirection.BOTTOM_LEFT]: 'BOTTOM_LEFT',
    [DockDirection.BOTTOM]: 'BOTTOM',
    [DockDirection.BOTTOM_RIGHT]: 'BOTTOM_RIGHT',
  };

  return directionMap[direction];
}

export function fromBaseUiDockDirection(direction: BaseUiDockDirection | string): DockDirection {
  const directionMap: Record<BaseUiDockDirection, DockDirection> = {
    TOP_LEFT: DockDirection.TOP_LEFT,
    TOP: DockDirection.TOP,
    TOP_RIGHT: DockDirection.TOP_RIGHT,
    LEFT: DockDirection.LEFT,
    MIDDLE: DockDirection.MIDDLE,
    RIGHT: DockDirection.RIGHT,
    BOTTOM_LEFT: DockDirection.BOTTOM_LEFT,
    BOTTOM: DockDirection.BOTTOM,
    BOTTOM_RIGHT: DockDirection.BOTTOM_RIGHT,
  };

  return directionMap[direction as BaseUiDockDirection];
}

export function toCssObjectPosition(position: DockDirection) {
  const focalPointMap = {
    [DockDirection.TOP_LEFT]: 'top left',
    [DockDirection.TOP]: 'top center',
    [DockDirection.TOP_RIGHT]: 'top right',
    [DockDirection.LEFT]: 'center left',
    [DockDirection.MIDDLE]: 'center',
    [DockDirection.RIGHT]: 'center right',
    [DockDirection.BOTTOM_LEFT]: 'bottom left',
    [DockDirection.BOTTOM]: 'bottom center',
    [DockDirection.BOTTOM_RIGHT]: 'bottom right',
  } as const satisfies Record<DockDirection, string>;

  return focalPointMap[position];
}

/**
 * Allows to derive style param values based on style param definitions
 *
 * @example
 * type StyleParams = {
 *  showAllPostsCategory: StyleParamType.Boolean;
 *  numberOfPosts: StyleParamType.Number;
 * }
 *
 * createStyleParams<StyleParams>({ ... });
 *
 * type StyleParamsValues = StyleParamsValues<StyleParams>;
 * // ^ type
 * // {
 * //   numbers: { numberOfPosts: number },
 * //   booleans: { showAllPostsCategory: boolean; }
 * // }
 */
export type StyleParamsValues<SP extends Record<string, StyleParamType>> = {
  numbers: {
    [K in keyof SP as SP[K] extends StyleParamType.Number ? K : never]: number;
  };
  booleans: {
    [K in keyof SP as SP[K] extends StyleParamType.Boolean ? K : never]: boolean;
  } & {
    /**
     * Provided by platform.
     * - If Studio: `true`
     * - If Classic: undefined
     **/
    responsive?: true;
  };
  colors: {
    [K in keyof SP as SP[K] extends StyleParamType.Color ? K : never]: string;
  };
  fonts: {
    [K in keyof SP as SP[K] extends StyleParamType.Font ? K : never]: FontValue;
  };
  strings: {
    [K in keyof SP as SP[K] extends StyleParamType.String ? K : never]: string;
  };
};

/** @returns Record<string, StyleParamType.Number | StyleParamType.Font | ...>  */
export type GetIStylesParams<T> = {
  [key in keyof T]: T[key] extends IStyleParam<infer IStyleParamType> ? IStyleParamType : never;
};
